// Supabase Auth wrapper — Phase B. Replaces the Phase A localStorage stub.
// Session persistence, refresh, and reset-password redirect handling are
// configured in src/lib/supabase.js.

import { supabase } from './supabase'

const RESET_REDIRECT = 'https://app.ghostlaundry.com/reset-password'

// --- Auth actions ----------------------------------------------------------

export async function signUp({
  email,
  password,
  firstName,
  lastName,
  phone,
  address_line1,
  address_line2,
  city,
  state,
  zip,
  hotel_name,
  room_number,
}) {
  const full_name = [firstName, lastName].filter(Boolean).join(' ').trim()
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    // options.data hydrates auth.users.raw_user_meta_data. The Postgres
    // trigger on auth.users insert reads these keys to seed the profiles
    // row: full_name, phone, address_line1, address_line2, city, state,
    // zip — plus hotel_name / room_number for hotel guests (spec §2).
    options: {
      data: {
        full_name,
        phone: phone || '',
        address_line1: address_line1 || '',
        address_line2: address_line2 || '',
        city: city || '',
        state: state || '',
        zip: zip || '',
        hotel_name: hotel_name || '',
        room_number: room_number || '',
      },
    },
  })
  if (error) throw error
  return data
}

export async function signIn({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function resetPasswordForEmail(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: RESET_REDIRECT,
  })
  if (error) throw error
}

// Called from /reset-password after Supabase detected the recovery token
// in the URL and established a temporary session.
export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) throw error
}

export async function getSession() {
  const { data } = await supabase.auth.getSession()
  return data.session
}

// Subscribe to auth changes. Returns the subscription so callers can unsub
// on unmount. Signature: (event, session) => void.
export function onAuthStateChange(callback) {
  const { data } = supabase.auth.onAuthStateChange(callback)
  return data.subscription
}

// --- Display helpers (used by Layout / Login / Profile) --------------------

export function initials(profile) {
  if (!profile) return '?'
  const first = (profile.first_name || '')[0] || ''
  const last = (profile.last_name || '')[0] || ''
  const combo = (first + last).toUpperCase()
  if (combo) return combo
  return (profile.email?.[0] || '?').toUpperCase()
}

export function fullName(profile) {
  if (!profile) return ''
  const n = `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
  return n || (profile.email || 'You')
}

export function formatAddress(profile) {
  const line1 = [profile?.address_line1, profile?.address_line2].filter(Boolean).join(', ')
  const line2 = [profile?.city, profile?.state, profile?.zip].filter(Boolean).join(', ')
  return { line1, line2, full: [line1, line2].filter(Boolean).join(' · ') }
}
